import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import { useRefresh } from '../../Hooks/useRefresh';
import { ImportContactsModal } from '../Tags/Tag';
import Modal from '../../Components/Modal';
import { ArrowDownTrayIcon, ArrowsPointingOutIcon, ArrowTopRightOnSquareIcon, MinusIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import JSONViewer from '../../Components/JSONViewer/JSONViewer';
import Stats from '../../Components/Stats';
import { TextInput } from '../../Components/Inputs/TextInput';
import { Button } from '../../Components/Buttons';
import { getTagEventName } from '../Automatisations/Automatisations';
import Select from '../../Components/Inputs/Select';

export const useEventMappingForTable = (events, tags) => {
    const { formatDate } = useDateFormat();
    const { translate } = useTranslation();
    return events.map((event) => {
        return { 
            ...event, 
            key: event.id, 
            created_at: formatDate(event.created_at, 'MMM dd HH:mm, yyyy'), 
            event_name_formatted: getTagEventName(event.name, tags, translate)
        };
    });
}

export default function Events() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();

    const appState = useAppState();
    const dispatch = useAppDispatch();
    const { sidebarSearch } = appState;

    const { formatDate } = useDateFormat();

    const [events, setEvents] = useState([]);
    const [fromDate, setFromDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [toDate, setToDate] = useState(() => {
        const date = new Date();
        date.setHours(23, 59, 59, 999);
        return date;
    });
    const [tags, setTags] = useState([]);
    const [eventName, setEventName] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const pagination = usePaginations();
    const {refresh, refreshId} = useRefresh();
    const [open, setOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const { page, pageSize } = pagination;
    const [stats, setStats] = useState([]);
    const [filters, setFilters] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();

    const havePrefilters = searchParams && searchParams.get('prefilters');
    const [didLoadPrefilters, setDidLoadPrefilters] = useState(false);

    useEffect(() => {
        pagination.setPage(1);
    }, [sidebarSearch]);

    useEffect(() => {
        api.listTags(1, 99999).then((response) => {
            setTags(response.data.tags);
        });
        
        if (searchParams && searchParams.get('prefilters')) {
            const { filters: decodedFilters, search: decodedSearch } = urlSafeBase64ToFiltersAndSearch(searchParams.get('prefilters'));
            setFilters(decodedFilters);
            console.log("decodedSearch", decodedSearch, "decodedFilters", decodedFilters);
            if (decodedSearch) {
                dispatch({ type: ActionTypes.SET_SIDEBAR_SEARCH, payload: decodedSearch });
            }

            setDidLoadPrefilters(true);
        }
    }, []);

    // Convert filters and search to URL-safe base64 string
    const filtersAndSearchToUrlSafeBase64 = (filters, search) => {
        try {
            const data = {
                filters: filters || [],
                search: search || ''
            };
            const jsonString = JSON.stringify(data);
            const base64 = btoa(jsonString);
            // Make URL-safe by replacing characters
            return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
        } catch (error) {
            console.error('Error converting filters and search to URL-safe base64:', error);
            return '';
        }
    };

    // Convert URL-safe base64 string back to filters and search
    const urlSafeBase64ToFiltersAndSearch = (base64String) => {
        try {
            if (!base64String) return { filters: [], search: '' };
            // Add padding back if needed
            let base64 = base64String.replace(/-/g, '+').replace(/_/g, '/');
            while (base64.length % 4) {
                base64 += '=';
            }
            const jsonString = atob(base64);
            const data = JSON.parse(jsonString);
            return {
                filters: data.filters || [],
                search: data.search || ''
            };
        } catch (error) {
            console.error('Error decoding URL-safe base64 filters and search:', error);
            return { filters: [], search: '' };
        }
    };

    // Get current filters and search as URL-safe base64
    const getFiltersAndSearchAsUrlSafeBase64 = () => {
        return filtersAndSearchToUrlSafeBase64(filters, sidebarSearch);
    };

    const handleSort = ({ key }) => {
        if (pagination.sortBy === key) {
            pagination.setSortOrder(pagination.sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            pagination.setSortBy(key);
            pagination.setSortOrder('asc');
        }
        pagination.setPage(1);
    }

    useEffect(() => {
        const fetchContacts = async () => {
            try {
                let newToDate = new Date(toDate);
                newToDate.setDate(newToDate.getDate() + 1);
                
                setLoading(true);
                const response = await api.listEvents(page, pageSize, formatDate(fromDate, "yyyy-MM-dd'T'HH:mm:ss", -1), formatDate(newToDate, "yyyy-MM-dd'T'HH:mm:ss", -1), sidebarSearch, pagination.sortBy, pagination.sortOrder, filters && filters.length > 0 ? filters : undefined );
                console.log("filters and search", getFiltersAndSearchAsUrlSafeBase64());
                
                setEvents(response.data.events);
                pagination.setMetaData(response.data.meta);
                if (response.data.stats) {
                    const totalEventsDelta = response.data.stats.total_events - response.data.previous_timerange_stats.total_events;
                    const uniqueEventDelta = response.data.stats.total_unique_events - response.data.previous_timerange_stats.total_unique_events;
                    const totalContactsDelta = response.data.stats.total_contacts - response.data.previous_timerange_stats.total_contacts;
                    const totalAutomatisationsDelta = response.data.stats.automatisation_trigger_count - response.data.previous_timerange_stats.automatisation_trigger_count;

                    const totalEventsDeltaPercentage = totalEventsDelta / response.data.previous_timerange_stats.total_events * 100;
                    const uniqueEventDeltaPercentage = uniqueEventDelta / response.data.previous_timerange_stats.total_unique_events * 100;
                    const totalContactsDeltaPercentage = totalContactsDelta / response.data.previous_timerange_stats.total_contacts * 100;
                    const totalAutomatisationsDeltaPercentage = totalAutomatisationsDelta / response.data.previous_timerange_stats.automatisation_trigger_count * 100;

                    const totalEventsDeltaString = totalEventsDelta > 0 ? `+${totalEventsDelta} (${totalEventsDeltaPercentage.toFixed(2)}%)` : `${totalEventsDelta} (${totalEventsDeltaPercentage.toFixed(2)}%)`;
                    const uniqueEventDeltaString = uniqueEventDelta > 0 ? `+${uniqueEventDelta} (${uniqueEventDeltaPercentage.toFixed(2)}%)` : `${uniqueEventDelta} (${uniqueEventDeltaPercentage.toFixed(2)}%)`;
                    const totalContactsDeltaString = totalContactsDelta > 0 ? `+${totalContactsDelta} (${totalContactsDeltaPercentage.toFixed(2)}%)` : `${totalContactsDelta} (${totalContactsDeltaPercentage.toFixed(2)}%)`;
                    const totalAutomatisationsDeltaString = totalAutomatisationsDelta > 0 ? `+${totalAutomatisationsDelta} (${totalAutomatisationsDeltaPercentage.toFixed(2)}%)` : `${totalAutomatisationsDelta} (${totalAutomatisationsDeltaPercentage.toFixed(2)}%)`;



                    setStats([
                        { name: translate('events.total_events'), value: response.data.meta.total_records, change: totalEventsDeltaString, changeType: totalEventsDelta > 0 ? 'positive' : 'negative' },
                        { name: translate('events.total_unique_events'), value: response.data.stats.total_unique_events, change: uniqueEventDeltaString, changeType: uniqueEventDelta > 0 ? 'positive' : 'negative' },
                        { name: translate('events.total_contacts'), value: response.data.stats.total_contacts, change: totalContactsDeltaString, changeType: totalContactsDelta > 0 ? 'positive' : 'negative' },
                        { name: translate('events.total_automatisations'), value: response.data.stats.automatisation_trigger_count, change: totalAutomatisationsDeltaString, changeType: totalAutomatisationsDelta > 0 ? 'positive' : 'negative' },
                    ]);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        if (havePrefilters && !didLoadPrefilters) {
            return;
        }

        fetchContacts();
    }, [pagination.page, pagination.sortBy, pagination.sortOrder, refreshId, sidebarSearch, didLoadPrefilters]);

    const mappedEvents = useEventMappingForTable(events, tags);

    const setToDateEndOfDay = (date) => {
        // Create date in local timezone to avoid timezone offset issues
        const [year, month, day] = date.split('-').map(Number);
        const newDate = new Date(year, month - 1, day, 0, 0, 0);
        setToDate(newDate);
        console.log("toDate", newDate);
    }

    const setFromDateStartOfDay = (date) => {
        // Create date in local timezone to avoid timezone offset issues
        const [year, month, day] = date.split('-').map(Number);
        const newDate = new Date(year, month - 1, day, 0, 0, 0);
        setFromDate(newDate);
        console.log("fromDate", newDate);
    }
    const variableSourceOptions = [
        { id: 'event', name: translate('automatisation.from_event') },
        { id: 'contact', name: translate('automatisation.from_contact') }
    ];

    const setFilterSource = (source, index) => {
        const newFilters = [...filters];

        const newVariableValueWithoutSource = newFilters[index].variable.split(".").slice(1).join(".");
        newFilters[index] = { ...newFilters[index], source: source.id, variable: source.id + "." + newVariableValueWithoutSource };
        setFilters(newFilters);
    }
    const addNewFilter = () => {
        setFilters([...filters, { source: variableSourceOptions[0].id, variable: variableSourceOptions[0].id + ".", operation: "EQUALS", value: "" }]);
    }

    const setFilterVariable = (variable, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], variable: newFilters[index].source + "." + variable };
        setFilters(newFilters);
    }

    const variableTypes = [
        { id: 1, name: translate('automatisation.string'), value: 'string' },
        { id: 2, name: translate('automatisation.number'), value: 'number' },
        { id: 3, name: translate('automatisation.boolean'), value: 'boolean' },
        { id: 4, name: translate('automatisation.string_variable'), value: 'string_variable' },
        { id: 5, name: translate('automatisation.number_variable'), value: 'number_variable' },
        { id: 6, name: translate('automatisation.boolean_variable'), value: 'boolean_variable' }
    ]

    const getOperationOptions = (variableType) => {
        const operations = [
            { id: 1, name: translate('automatisation.is_equal'), value: 'EQUALS' },
            { id: 2, name: translate('automatisation.is_not_equal'), value: 'NOT_EQUALS' },
            ...(variableType === 'number' || variableType === 'number_variable' ? [{ id: 3, name: translate('automatisation.is_greater'), value: 'GREATER_THAN' },{ id: 4, name: translate('automatisation.is_less'), value: 'LESS_THAN' }] : []),
            ...(variableType === 'string' || variableType === 'string_variable' ? [{ id: 5, name: translate('automatisation.contains'), value: 'CONTAINS' }, { id: 6, name: translate('automatisation.does_not_contain'), value: 'NOT_CONTAINS' }] : []),
            ...(variableType === 'number_variable' || variableType === 'string_variable' || variableType === 'boolean_variable' ? [] : [{ id: 7, name: translate('automatisation.is_null_or_empty'), value: 'IS_NULL_OR_EMPTY' }, { id: 8, name: translate('automatisation.is_not_null_or_empty'), value: 'IS_NOT_NULL_OR_EMPTY' }]),
        ];
        return operations;
    };

    const setFilterOperation = (operation, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], operation: operation, variable_type: variableTypes.find(type => type.value === newFilters[index].variable_type)?.value || variableTypes[0].value };
        setFilters(newFilters);
    }
    const setFilterValueSource = (source, index) => {
        const newFilters = [...filters];
        const newVariableValueWithoutSource = newFilters[index].value.split(".").slice(1).join(".");
        newFilters[index] = { ...newFilters[index], value_source: source.id, value: source.id + "." + newVariableValueWithoutSource };
        setFilters(newFilters);
    }
    const setFilterValue = (value, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], value: newFilters[index].value_source + "." + value };
        setFilters(newFilters);
    }

    const setFilterValueWithoutSource = (value, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], value: value };
        setFilters(newFilters);
    }

    const getSelectedFilterOperation = (filter) => {
        return getOperationOptions(filter.variable_type || variableTypes[0].value).find(operation => operation.value === filter.operation) || getOperationOptions(filter.variable_type || variableTypes[0].value)[0];
    }

    const setFilterVariableType = (variableType, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], variable_type: variableType, value: "", operation: "EQUALS" };
        setFilters(newFilters);
    }

    const removeFilter = (index) => {
        const newFilters = [...filters];
        newFilters.splice(index, 1);
        setFilters(newFilters);
    }

    return (
        <>
            <Modal open={open} 
                onClose={() => setOpen(false)} 
                maxWidth="sm:max-w-2xl"
                title={translate('events.event_properties')} 
                description={translate('events.event_properties_description')} 
                children={<div className="max-h-[70vh] overflow-y-auto">
                    <JSONViewer data={selectedEvent ? selectedEvent.data : null} />
                </div>} 
            />
            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="events">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                        <LoadingCircleTransparent />
                    </div> : (
                        <>
                        <div className="flex flex-row justify-between items-center">
                            <div className="flex flex-row items-end gap-2 justify-end">
                                <TextInput type="date" label={translate('events.from_date')} value={formatDate(fromDate, "yyyy-MM-dd", 0)} onChange={(value) => setFromDateStartOfDay(value.target.value)} />
                                <TextInput type="date" label={translate('events.to_date')} value={formatDate(toDate, "yyyy-MM-dd", 0)} onChange={(value) => setToDateEndOfDay(value.target.value)} />
                                <div className="flex flex-row justify-end">
                                    <Button  onClick={() => {
                                        refresh();
                                    }}>{translate('events.fetch')}</Button>
                                </div>
                                
                            </div>
                        </div>
                        <div className="flex flex-row mt-4 text-gray-900 items-center cursor-pointer hover:text-gray-600" onClick={() => addNewFilter()}>
                            <p className="text-sm font-bold">{translate('filters').slice(0,1).toUpperCase() + translate('filters').slice(1)}</p>
                            <PlusIcon className="w-5 h-5  ml-2" />
                        </div>
                        <div className="flex flex-col">
                            {filters.map((filter, index) => (
                                <div className="flex flex-row items-center gap-x-2">
                                    <div className='w-48'>
                                        <Select options={variableSourceOptions} selected={variableSourceOptions.find(source => source.id === filter.source) || variableSourceOptions[0]} setSelected={(e) => setFilterSource(e, index)} />
                                    </div>
                                    <div className="w-6 bg-gray-200 h-[1px]"></div>
                                    <TextInput placeholder={translate('automatisation.variable')} value={filter.variable.split(".").slice(1).join(".")} onChange={(e) => setFilterVariable(e.target.value, index)} />
                                    <div className="w-6 bg-gray-200 h-[1px]"></div>
                                    <div className='w-48'>
                                        <Select options={getOperationOptions(filter.variable_type || variableTypes[0].value)} selected={getSelectedFilterOperation(filter)} setSelected={(e) => setFilterOperation(e.value, index)} />
                                    </div>
                                    <div className="w-6 bg-gray-200 h-[1px]"></div>
                                    <div className='w-48'>
                                        <Select options={variableTypes} selected={variableTypes.find(type => type.value === filter.variable_type) || variableTypes[0]} setSelected={(e) => setFilterVariableType(e.value, index)} />
                                    </div>
                                    <div className="w-6 bg-gray-200 h-[1px]"></div>
                                    {(filter.variable_type === 'string_variable' || filter.variable_type === 'number_variable' || filter.variable_type === 'boolean_variable') && <div className="flex flex-row items-center gap-x-2">
                                        <div className='w-48'>
                                            <Select options={variableSourceOptions} selected={variableSourceOptions.find(source => source.id === filter.value_source) || variableSourceOptions[0]} setSelected={(e) => setFilterValueSource(e, index)} />
                                        </div>
                                        <div className="w-6 bg-gray-200 h-[1px]"></div>
                                        
                                        <TextInput value={filter.value.split(".").slice(1).join(".")} onChange={(e) => setFilterValue(e.target.value, index)} />
                                    </div>}
                                    {(!filter.variable_type?.includes('variable') && getSelectedFilterOperation(filter).value != 'IS_NULL_OR_EMPTY' && getSelectedFilterOperation(filter).value != 'IS_NOT_NULL_OR_EMPTY') && <div className="flex flex-row items-center gap-x-2">
                                        <TextInput value={filter.value} onChange={(e) => setFilterValueWithoutSource(e.target.value, index)} />
                                    </div>}
                                    <MinusIcon className="w-5 h-5 text-red-500 cursor-pointer hover:text-red-300" onClick={() => removeFilter(index)} />
                                </div>
                            ))}
                        </div>
                         <div className="flex flex-row mb-4">
                            <div className="mt-8 flex flex-col border border-gray-200 w-full">
                                <Stats stats={stats} />
                            </div>
                        </div>
                        <Table
                            title={translate('events.title')}
                            description={translate('events.description')}
                            createButton={undefined}
                            data={mappedEvents}
                            translationKey="events.table"
                            columns={['event_name_formatted', 'contact_external_id', 'created_at']}
                            pagination={pagination}
                            onSort={handleSort}
                            onRowClick={(row) => {
                                setSelectedEvent(row);
                                setOpen(true);
                            }}
                            dropdownGroups={[{
                                options: [{
                                    label: translate('events.viewContact'),
                                    icon: <ArrowTopRightOnSquareIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => {
                                        if (row && row.contact_external_id) {
                                            navigate(`/contacts/${row.contact_external_id}`);
                                        }
                                    }
                                }]
                            }]}
                        />
                        </>
                    )}
                </SidebarNav>
            </div>
        </>
    );
}